'use client';

import {Button} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';

import styles from './community-header.module.scss';

interface CommunityHeaderProps {
    /**
     * Community display name — rendered as the section title. Presentational
     * only; the public profile page (PUBLISHED) and the Phase-2 instructor
     * preview tab (DRAFT) both pass already-resolved data here.
     */
    name?: string | null;
    /** One-line core promise — rendered as the subtitle. */
    corePromise?: string | null;
    /** Community URL for the CTA. */
    communityUrl?: string | null;
    /**
     * Click handler for the CTA. Optional; defaults to a no-op when
     * communityUrl is not provided.
     */
    onJoinClick?: () => void;
    /**
     * Whether the viewer is already subscribed to the community. When true the
     * CTA reads "Open Community" (and the caller passes the base community URL)
     * instead of the "Join my community" promo for non-members.
     */
    isSubscribed?: boolean;
}

function buildJoinUrl(communityUrl: string): string {
    const url = new URL(communityUrl);
    url.searchParams.set('automatic_login', 'true');
    return url.toString();
}

const noop = () => {};

/**
 * Header division of the community section (Figma node 2944:40169): centred
 * community title, core-promise subtitle, and a primary CTA. Non-members see
 * "Join my community"; already-subscribed viewers see "Open Community". When
 * communityUrl is provided, clicking the button navigates to the community with
 * automatic_login=true search param appended.
 */
export function CommunityHeader({
    name,
    corePromise,
    communityUrl,
    onJoinClick,
    isSubscribed = false,
}: CommunityHeaderProps) {
    const {gettext} = useI18n();

    const handleJoinClick = onJoinClick ?? (communityUrl ? () => {
        const joinUrl = buildJoinUrl(communityUrl);
        window.location.href = joinUrl;
    } : noop);

    return (
        <div className={styles['community-header']}>
            <div className={styles['text-block']}>
                {name ? <h1 className={styles['title']}>{name}</h1> : null}
                {corePromise ? <p className={styles['promise']}>{corePromise}</p> : null}
            </div>
            <Button
                udStyle="primary"
                size="large"
                className={styles['join-button']}
                onClick={handleJoinClick}
                data-purpose="join-community"
            >
                {isSubscribed ? gettext('Open Community') : gettext('Join my community')}
            </Button>
        </div>
    );
}
